/**
 * HTML generation from Notion blocks.
 * Pure functions — no DOM or Office.js access.
 * @module utils/html
 */

import { NOTION_COLORS, NOTION_BG } from "./colors";

/**
 * Escapes HTML special characters in a string.
 * @param {string} s
 * @returns {string}
 */
export function escHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Sanitizes a URL: blocks dangerous schemes (javascript:, data:, vbscript:).
 * Returns empty string for unsafe URLs.
 * @param {string} url
 * @returns {string}
 */
export function sanitizeUrl(url) {
  if (!url) return "";
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith("javascript:") || trimmed.startsWith("vbscript:") || trimmed.startsWith("data:")) {
    return "";
  }
  return url;
}

/**
 * Renders Notion rich text spans to HTML with inline formatting.
 * @param {Array<{text: string, bold?: boolean, italic?: boolean, underline?: boolean, strikethrough?: boolean, code?: boolean, color?: string}>} spans
 * @param {string} fallback - Text to use if spans are empty
 * @returns {string} HTML string
 */
export function renderRichText(spans, fallback) {
  if (!spans || !spans.length) return escHtml(fallback || "");
  return spans.map(s => {
    let h = escHtml(s.text);
    if (s.bold)          h = `<strong>${h}</strong>`;
    if (s.italic)        h = `<em>${h}</em>`;
    if (s.underline)     h = `<u>${h}</u>`;
    if (s.strikethrough) h = `<s>${h}</s>`;
    if (s.code)          h = `<code style="font-family:Consolas,monospace;background:#f0f0f0;padding:1px 4px;border-radius:2px;font-size:0.9em">${h}</code>`;
    const col = NOTION_COLORS[s.color];
    if (col) h = `<span style="color:${col}">${h}</span>`;
    return h;
  }).join("");
}

/**
 * Returns inline CSS from a block's color annotation.
 * @param {{block_color?: string}} b
 * @returns {string} CSS string
 */
export function blockColorStyle(b) {
  if (!b.block_color || b.block_color === "default") return "";
  if (b.block_color.endsWith("_background")) {
    const bg = NOTION_BG[b.block_color];
    return bg ? `background:${bg};padding:2px 4px;border-radius:2px;` : "";
  }
  const fg = NOTION_COLORS[b.block_color];
  return fg ? `color:${fg};` : "";
}

/**
 * Extracts plain text from a table cell (rich text array or string).
 * @param {Array<{text: string}>|string} cell
 * @returns {string}
 */
export function cellPlainText(cell) {
  if (Array.isArray(cell)) return cell.map(s => (s && s.text) ? s.text : "").join("");
  return cell || "";
}

/**
 * Converts a table cell to HTML, preserving rich text annotations.
 * @param {Array|string} cell
 * @returns {string} HTML string
 */
export function cellHtml(cell) {
  if (Array.isArray(cell) && cell.length) {
    return `<span style="text-decoration:none;">${renderRichText(cell, "")}</span>`;
  }
  return `<span style="text-decoration:none;">${escHtml(typeof cell === "string" ? cell : "")}</span>`;
}

/**
 * Converts a single Notion block to HTML.
 * @param {object} b - Normalized Notion block
 * @param {object} imgCache - Image cache (block ID → data URI)
 * @param {string} [backend=""] - Backend URL prefix for media
 * @returns {string} HTML string
 */
export function blockToHtml(b, imgCache, backend = "") {
  const cs = blockColorStyle(b);

  switch (b.type) {

    case "heading_1":
      return `<h1 style="${cs}">${renderRichText(b.rich_text, b.text)}</h1>`;

    case "heading_2":
      return `<h2 style="${cs}">${renderRichText(b.rich_text, b.text)}</h2>`;

    case "heading_3":
      return `<h3 style="${cs}">${renderRichText(b.rich_text, b.text)}</h3>`;

    case "paragraph": {
      if (!b.text && (!b.rich_text || !b.rich_text.length)) return "<p>&nbsp;</p>";
      return `<p style="${cs}">${renderRichText(b.rich_text, b.text)}</p>`;
    }

    case "quote":
      return `<blockquote style="border-left:3px solid #ccc;padding-left:10px;color:#555;margin:8px 0;${cs}">${renderRichText(b.rich_text, b.text)}</blockquote>`;

    case "code": {
      const lang = b.language
        ? `<div style="font-size:10px;color:#888;margin-bottom:4px">${escHtml(b.language)}</div>`
        : "";
      return `<div style="background:#1e1e1e;color:#e8e8e8;font-family:Consolas,monospace;font-size:12px;padding:12px;margin:8px 0;border-radius:4px">${lang}<pre style="margin:0;white-space:pre-wrap">${escHtml(b.text || "")}</pre></div>`;
    }

    case "callout": {
      const icon = b.icon ? escHtml(b.icon) + " " : "";
      const bg   = b.block_color && b.block_color.endsWith("_background")
        ? NOTION_BG[b.block_color] || "#f7f7f7"
        : "#f7f7f7";
      const children = b.children ? blocksToHtml(b.children, imgCache, backend) : "";
      return `<div style="background:${bg};padding:10px 14px;border-radius:4px;margin:8px 0">${icon}${renderRichText(b.rich_text, b.text)}${children}</div>`;
    }

    case "to_do": {
      const mark  = b.checked ? "☑" : "☐";
      const deco  = b.checked ? "text-decoration:line-through;color:#999;" : "";
      return `<p style="${deco}${cs}">${mark} ${renderRichText(b.rich_text, b.text)}</p>`;
    }

    case "toggle": {
      const children = b.children ? blocksToHtml(b.children, imgCache, backend) : "";
      return `<p style="${cs}"><strong>▶ ${renderRichText(b.rich_text, b.text)}</strong></p>${children}`;
    }

    case "image":
      return "";

    case "table": {
      if (!b.rows || !b.rows.length) return "";
      const rows = b.rows.map((row, ri) => {
        const isHeader = b.has_column_header && ri === 0;
        const tag = isHeader ? "th" : "td";
        const cellStyle = isHeader
          ? "border:1px solid #bbb;padding:3px 8px;background:#f0f0f0;font-weight:bold;font-size:10px;white-space:nowrap"
          : "border:1px solid #bbb;padding:3px 8px;font-size:10px;white-space:nowrap";
        const cellContent = (c) => isHeader ? escHtml(c).replace(/ /g, "\u00A0") : escHtml(c);
        return `<tr>${row.map(c => `<${tag} style="${cellStyle}">${cellContent(c)}</${tag}>`).join("")}</tr>`;
      }).join("");
      return `<table style="border-collapse:collapse;width:auto;table-layout:auto;margin:8px 0">${rows}</table>`;
    }

    case "divider":
      return `<div style="page-break-before: always;"></div>`;

    case "video": {
      const url   = sanitizeUrl(b.local_path
        ? `${backend}/api/media/${b.local_path.replace(/\\/g, "/")}`
        : (b.url || ""));
      const label = b.caption || url;
      return url ? `<p>📹 <a href="${escHtml(url)}">${escHtml(label)}</a></p>` : "";
    }

    case "bookmark":
    case "embed":
    case "link_preview": {
      const href = sanitizeUrl(b.url || "");
      return href ? `<p>🔗 <a href="${escHtml(href)}">${escHtml(href)}</a></p>` : "";
    }

    case "equation":
      return `<p style="font-style:italic;font-family:serif">∑ ${escHtml(b.expression || "")}</p>`;

    case "child_page":
      return `<p>📄 ${escHtml(b.title || "")}</p>`;

    case "child_database":
      return `<p>🗃 ${escHtml(b.title || "")}</p>`;

    case "column_list":
    case "column":
      return b.children ? blocksToHtml(b.children, imgCache, backend) : "";

    case "synced_block":
      return b.children ? blocksToHtml(b.children, imgCache, backend) : "";

    case "table_of_contents":
      return "";

    default:
      return b.text ? `<p style="${cs}">${escHtml(b.text)}</p>` : "";
  }
}

/**
 * Converts an array of Notion blocks to HTML, grouping consecutive list items.
 * @param {Array<object>} blocks
 * @param {object} imgCache - Image cache (block ID → data URI)
 * @param {string} [backend=""] - Backend URL prefix for media
 * @returns {string} HTML string
 */
export function blocksToHtml(blocks, imgCache, backend = "") {
  let html = "";
  let i    = 0;

  while (i < blocks.length) {
    const b = blocks[i];

    if (b.type === "bulleted_list_item") {
      html += "<ul>";
      while (i < blocks.length && blocks[i].type === "bulleted_list_item") {
        html += `<li style="${blockColorStyle(blocks[i])}">`;
        html += renderRichText(blocks[i].rich_text, blocks[i].text);
        if (blocks[i].children) html += blocksToHtml(blocks[i].children, imgCache, backend);
        html += "</li>";
        i++;
      }
      html += "</ul>";

    } else if (b.type === "numbered_list_item") {
      html += "<ol>";
      while (i < blocks.length && blocks[i].type === "numbered_list_item") {
        html += `<li style="${blockColorStyle(blocks[i])}">`;
        html += renderRichText(blocks[i].rich_text, blocks[i].text);
        if (blocks[i].children) html += blocksToHtml(blocks[i].children, imgCache, backend);
        html += "</li>";
        i++;
      }
      html += "</ol>";

    } else {
      html += blockToHtml(b, imgCache, backend);
      i++;
    }
  }

  return html;
}
